import { styled } from '@mui/material/styles';
import React from 'react';
import { brandclub_colors } from '../../../colors';
import { Button } from '../../../Components/Button';
import { ArrowRightIcon, SuccessCheckCircleIcon } from '../../../icons';
import { BrandClub } from '../../../types';
import { escapeUriPath, formatMoney } from '../../../utils';
import { MOBILE_SCREEN_SIZE } from '../../../utils/constants';
import { ButtonLoadingEllipsis } from '../Loading';
import { BaseComponentPropsType } from '../../type';

type Brand = Partial<TopBrand & BrandClub>;
type JoinedState = 'not_join' | 'joining' | 'joined';

interface TopBrand {
  brandId: number;
  brandclubId: string;
  rewardAmount?: number;
  name: string;
  imageUrl: string;
  expiringRewardAmount?: number;
}

interface BrandCardProps {
  brand: Brand;
  joined?: boolean;
  reward: number;
  onJoin(e: React.MouseEvent): Promise<void>;
  joinedState: JoinedState;
  LinkComponent: BaseComponentPropsType['LinkComponent'];
}

interface ProductBrandCardButtonProps {
  joined: boolean | undefined;
  joinedState: JoinedState;
  brand: Brand;
  onJoin(e: React.MouseEvent): Promise<void>;
}

interface BrandCardJoinMessageProps {
  joinedState: JoinedState;
  reward: number;
  joined: boolean | undefined;
  brand: Brand;
  LinkComponent: BaseComponentPropsType['LinkComponent'];
}

const BrandCardContainer = styled('div')(() => ({
  border: 'solid 1px #dcdddc',
  width: '100%',
  height: '100%',
  borderRadius: 12,
  // matches product card padding
  padding: '16px 16px 16px 22px',
  paddingTop: 58,
  cursor: 'pointer',
  boxSizing: 'border-box',
  transition: 'background-color 0.4s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0,
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: 10,
    paddingTop: 52,
    width: '100%',
  },
}));

const BrandLogoContainer = styled('div')(() => ({
  width: 'calc(100% - 24px)',
  maxWidth: '190px',
  aspectRatio: '1/1',
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const BrandLogoImg = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: 149,
  aspectRatio: '1/1',
  backgroundColor: theme.palette.common.white,
  objectFit: 'contain',
  borderRadius: 999,
}));

const BrandName = styled('h2')(({ theme }) => ({
  fontSize: 22,
  fontWeight: 600,
  marginBottom: 16.7,
  marginTop: 20,
  color: theme.palette.primary.main,
  overflow: 'hidden',
  width: '80%',
  textAlign: 'center',
  textDecoration: 'none',
  letterSpacing: 0.36,
  WebkitLineClamp: 1,
  lineHeight: 1.5,
  height: `1.5em`, // line height * 2
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  wordBreak: 'break-word',
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 16,
  },
}));

const BrandCardStatusMessage = styled('div')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  marginTop: 29.3,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',

  textAlign: 'center',
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 12,
  },
  '.arrow': {
    width: 13,
    height: 13,
    objectFit: 'contain',
    marginLeft: 10,
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: 10,
      height: 10,
    },
  },
}));

const BrandCardButton = styled(Button)(({ theme }) => ({
  backgroundColor: brandclub_colors.white,
  color: theme.palette.primary.main,
  border: `1.5px solid ${theme.palette.primary.main}`,
  height: 40,
  fontSize: 17,
  fontWeight: 600,
  minWidth: 108,
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    height: 30,
    fontSize: 12,
    minWidth: 88,
  },
}));

const BrandCardButtonSolid = styled(BrandCardButton)(() => ({
  backgroundColor: brandclub_colors.grayF3F4F5,
  border: `1.5px solid ${brandclub_colors.grayF3F4F5}`,
}));

const getBrandActivityPath = (brand: Brand) =>
  `/${escapeUriPath(brand.name)}/b/${brand.brandId}/branddashboard/rewards/activity`;
const getBrandHomePath = (brand: Brand) => `/${escapeUriPath(brand.name)}/b/${brand.brandId}/home`;

const BrandCardJoinButton = (props: ProductBrandCardButtonProps) => {
  if (props.joined) {
    return <BrandCardButton>{formatMoney(props.brand?.rewardAmount ?? 0)}</BrandCardButton>;
  }
  if (props.joinedState === 'not_join') {
    return <BrandCardButtonSolid onClick={props.onJoin}>Join</BrandCardButtonSolid>;
  }

  if (props.joinedState === 'joining') {
    return (
      <BrandCardButtonSolid>
        Joining
        <ButtonLoadingEllipsis baseFontSize={17} blue></ButtonLoadingEllipsis>
      </BrandCardButtonSolid>
    );
  }

  return (
    <BrandCardButton>
      <SuccessCheckCircleIcon className="button_img" />
      Member
    </BrandCardButton>
  );
};

const BrandCardJoinMessage = (props: BrandCardJoinMessageProps) => {
  const { brand, LinkComponent } = props;
  const viewActivityPath = getBrandActivityPath(brand);
  const homePath = getBrandHomePath(brand);

  if (props.joinedState === 'joined' && props.reward) {
    return (
      <BrandCardStatusMessage>{`You earned ${formatMoney(props.reward)} by joining this club`}</BrandCardStatusMessage>
    );
  }

  return (
    <LinkComponent
      to={props.joined ? viewActivityPath : homePath}
      style={{
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <BrandCardStatusMessage>
        <span>{props.joined ? 'View activity' : `Visit ${brand.name}`}</span>
        <ArrowRightIcon className="arrow" color="primary" />
      </BrandCardStatusMessage>
    </LinkComponent>
  );
};

const ProductBrandCard = ({ brand, joined, joinedState, reward, LinkComponent, ...props }: BrandCardProps) => {
  return (
    <BrandCardContainer>
      <LinkComponent
        to={getBrandHomePath(brand)}
        style={{
          textDecoration: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <BrandLogoContainer>
          <BrandLogoImg alt="logo" src={brand.imageUrl} />
        </BrandLogoContainer>
        <BrandName>{brand.name}</BrandName>
        <BrandCardJoinButton brand={brand} joined={joined} onJoin={props.onJoin} joinedState={joinedState} />
      </LinkComponent>
      <BrandCardJoinMessage
        joinedState={joinedState}
        reward={reward}
        joined={joined}
        brand={brand}
        LinkComponent={LinkComponent}
      />
    </BrandCardContainer>
  );
};

export default ProductBrandCard;
